import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "../lib/api";
import MediaPicker from "./MediaPicker";

const EMPTY = {
  brand_name: "", full_name: "", tagline: "",
  profile_photo_alt: "", about_photo_alt: "",
  socials: { instagram: "", twitter: "", youtube: "", linkedin: "", tiktok: "" },
  contact: { email: "", whatsapp: "", address: "" },
  seo: { site_url: "", title_suffix: "", default_description: "" },
  lynk: { base_url: "" },
  footer: { note: "" },
  categories: [], post_types: [], book_types: [],
};

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [emailActive, setEmailActive] = useState(true);
  const [picker, setPicker] = useState(null);
  const [importData, setImportData] = useState(null);
  const importRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => setForm({ ...EMPTY }));
    api.get("/admin/dashboard").then((r) => setEmailActive(!!r.data.email_active)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSub = (obj, k, v) => setForm((f) => ({ ...f, [obj]: { ...f[obj], [k]: v } }));

  const save = async () => {
    setState("saving");
    setError("");
    try {
      await api.put("/admin/settings", { content: form });
      setState("saved");
      setTimeout(() => setState("idle"), 2000);
    } catch (err) {
      setState("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const exportAll = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `heri-sensei-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        if (!data.articles || !data.pages) {
          setError("Berkas pemulihan tidak valid (harus berisi articles dan pages).");
          return;
        }
        setImportData(data);
      } catch {
        setError("Berkas bukan JSON yang valid.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  if (!form) return <p className="text-sub text-sm">Memuat…</p>;

  const S = "bg-surface border border-line rounded-lg p-5 space-y-4";

  return (
    <div data-testid="admin-settings-page" className="max-w-2xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-serif text-3xl text-ink">Pengaturan</h1>
        <div className="flex items-center gap-3">
          {state === "saved" && <span data-testid="settings-saved-indicator" className="text-[13px] font-semibold text-moss">Tersimpan</span>}
          <button onClick={save} data-testid="settings-save-button" className="btn-primary !min-h-[40px]">
            {state === "saving" && <Loader2 size={15} className="animate-spin" />} Simpan
          </button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-enji mt-3">{error}</p>}

      <div className="mt-6 space-y-5">
        <div className={S}>
          <p className="font-semibold text-ink text-sm">Brand &amp; Identitas</p>
          <div>
            <label className="field-label" htmlFor="st-brand">Nama brand (wordmark)</label>
            <input id="st-brand" data-testid="settings-brand-input" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-full">Nama lengkap dengan gelar</label>
            <input id="st-full" data-testid="settings-fullname-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-tagline">Tagline</label>
            <input id="st-tagline" data-testid="settings-tagline-input" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-newsletter-label">Label tombol newsletter</label>
            <input id="st-newsletter-label" data-testid="settings-newsletter-label" value={form.newsletter_label || ""} onChange={(e) => set("newsletter_label", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-quote">Kalimat bijak (pita bawah halaman)</label>
            <input id="st-quote" data-testid="settings-quote-input" value={form.quote || ""} onChange={(e) => set("quote", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-quote-src">Sumber kalimat (opsional)</label>
            <input id="st-quote-src" data-testid="settings-quote-source-input" value={form.quote_source || ""} onChange={(e) => set("quote_source", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-footer-note">Catatan brand di footer</label>
            <input id="st-footer-note" data-testid="settings-footer-note" value={form.footer?.note || ""} onChange={(e) => setSub("footer", "note", e.target.value)} className="field-input" />
          </div>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                {form.profile_photo_id ? <img src={mediaUrl(form.profile_photo_id)} alt="" className="w-16 h-20 object-cover rounded border border-line" /> : <div className="w-16 h-20 rounded bg-surface-muted border border-line" />}
                <div className="space-y-2 min-w-0 flex-1">
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" data-testid="settings-profile-photo" onClick={() => setPicker("profile")} className="btn-ghost !min-h-[40px] !px-4 text-sm">Foto hero/profil (4:5)</button>
                    {form.profile_photo_id && <button type="button" data-testid="settings-profile-photo-remove" onClick={() => set("profile_photo_id", "")} className="text-[13px] text-enji">Hapus</button>}
                  </div>
                  <input data-testid="settings-profile-photo-alt" value={form.profile_photo_alt || ""} onChange={(e) => set("profile_photo_alt", e.target.value)} placeholder="Alt text foto profil" className="field-input !min-h-[40px] text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                {form.about_photo_id ? <img src={mediaUrl(form.about_photo_id)} alt="" className="w-16 h-20 object-cover rounded border border-line" /> : <div className="w-16 h-20 rounded bg-surface-muted border border-line" />}
                <div className="space-y-2 min-w-0 flex-1">
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" data-testid="settings-about-photo" onClick={() => setPicker("about")} className="btn-ghost !min-h-[40px] !px-4 text-sm">Foto Tentang Saya (4:5)</button>
                    {form.about_photo_id && <button type="button" data-testid="settings-about-photo-remove" onClick={() => set("about_photo_id", "")} className="text-[13px] text-enji">Hapus</button>}
                  </div>
                  <input data-testid="settings-about-photo-alt" value={form.about_photo_alt || ""} onChange={(e) => set("about_photo_alt", e.target.value)} placeholder="Alt text foto Tentang" className="field-input !min-h-[40px] text-sm" />
                </div>
              </div>
          </div>
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">Sosial Media — hanya diisi yang benar-benar tersedia</p>
          {["instagram", "twitter", "youtube", "linkedin", "tiktok"].map((k) => (
            <div key={k}>
              <label className="field-label" htmlFor={`st-${k}`}>{k}</label>
              <input id={`st-${k}`} data-testid={`settings-social-${k}`} value={form.socials?.[k] || ""} onChange={(e) => setSub("socials", k, e.target.value)} className="field-input !min-h-[40px]" placeholder={k} />
            </div>
          ))}
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">Kontak — kanal kosong tidak akan ditampilkan sebagai tautan</p>
          <div>
            <label className="field-label" htmlFor="st-email">Email</label>
            <input id="st-email" data-testid="settings-contact-email" value={form.contact?.email || ""} onChange={(e) => setSub("contact", "email", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-wa">WhatsApp (format 62…)</label>
            <input id="st-wa" data-testid="settings-contact-whatsapp" value={form.contact?.whatsapp || ""} onChange={(e) => setSub("contact", "whatsapp", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-addr">Alamat</label>
            <input id="st-addr" data-testid="settings-contact-address" value={form.contact?.address || ""} onChange={(e) => setSub("contact", "address", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">SEO Dasar</p>
          <div>
            <label className="field-label" htmlFor="st-site">URL situs</label>
            <input id="st-site" data-testid="settings-seo-siteurl" value={form.seo?.site_url || ""} onChange={(e) => setSub("seo", "site_url", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-suffix">Akhiran judul tab</label>
            <input id="st-suffix" data-testid="settings-seo-suffix" value={form.seo?.title_suffix || ""} onChange={(e) => setSub("seo", "title_suffix", e.target.value)} className="field-input !min-h-[40px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-desc">Deskripsi bawaan</label>
            <textarea id="st-desc" data-testid="settings-seo-desc" value={form.seo?.default_description || ""} onChange={(e) => setSub("seo", "default_description", e.target.value)} className="field-input !min-h-[70px]" />
          </div>
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">Lynk.id &amp; Kategori</p>
          <div>
            <label className="field-label" htmlFor="st-lynk">URL dasar Lynk.id (opsional)</label>
            <input id="st-lynk" data-testid="settings-lynk-base" value={form.lynk?.base_url || ""} onChange={(e) => setSub("lynk", "base_url", e.target.value)} className="field-input !min-h-[40px]" placeholder="https://lynk.id/akunanda" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-cats">Kategori tulisan (satu per baris)</label>
            <textarea id="st-cats" data-testid="settings-categories" value={(form.categories || []).join("\n")} onChange={(e) => set("categories", e.target.value.split("\n").filter((x) => x.trim()))} className="field-input !min-h-[120px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-types">Jenis tulisan (satu per baris)</label>
            <textarea id="st-types" data-testid="settings-posttypes" value={(form.post_types || []).join("\n")} onChange={(e) => set("post_types", e.target.value.split("\n").filter((x) => x.trim()))} className="field-input !min-h-[90px]" />
          </div>
          <div>
            <label className="field-label" htmlFor="st-btypes">Jenis karya (satu per baris)</label>
            <textarea id="st-btypes" data-testid="settings-booktypes" value={(form.book_types || []).join("\n")} onChange={(e) => set("book_types", e.target.value.split("\n").filter((x) => x.trim()))} className="field-input !min-h-[90px]" />
          </div>
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">Layanan Eksternal</p>
          <p className="text-[13px] text-sub" data-testid="settings-email-status">
            Email (Resend, dikelola Emergent): {emailActive ? <span className="text-moss font-semibold">aktif</span> : <span className="text-enji font-semibold">belum aktif</span>}
          </p>
          <p className="text-[13px] text-faint">Checkout dilakukan sepenuhnya di Lynk.id — tidak ada pembayaran di dalam situs ini.</p>
        </div>

        <div className={S}>
          <p className="font-semibold text-ink text-sm">Ekspor &amp; Pemulihan</p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={exportAll} data-testid="settings-export-button" className="btn-ghost !min-h-[40px] !px-4 text-sm"><Download size={15} /> Ekspor konten (JSON)</button>
            <label className="btn-ghost !min-h-[40px] !px-4 text-sm cursor-pointer">
              <Upload size={15} /> Pulihkan dari berkas
              <input ref={importRef} type="file" accept="application/json" className="hidden" onChange={onImportFile} />
            </label>
          </div>
          <p className="text-[13px] text-faint">Ekspor subscriber tersedia di menu Newsletter (CSV, khusus pemilik).</p>
        </div>
      </div>

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onPick={(m) => { set(picker === "profile" ? "profile_photo_id" : "about_photo_id", m.id); setPicker(null); }} />

      {importData && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" onClick={() => setImportData(null)}>
          <div className="bg-ivory border border-line rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl text-ink">Konfirmasi Pemulihan</h2>
            <p className="text-sub text-sm mt-3" data-testid="import-summary">
              Berkas berisi {importData.articles?.length || 0} tulisan, {importData.books?.length || 0} karya, {importData.pages?.length || 0} halaman.
              Konten saat ini akan DITIMPA. Lanjutkan?
            </p>
            <div className="flex gap-3 mt-5">
              <button
                data-testid="import-confirm-button"
                onClick={async () => {
                  try {
                    await api.post("/admin/import", importData);
                    setImportData(null);
                    setState("saved");
                    setTimeout(() => setState("idle"), 2000);
                  } catch (err) {
                    setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                    setImportData(null);
                  }
                }}
                className="btn-primary !min-h-[40px] !px-5 text-sm flex-1"
              >
                Ya, Timpa
              </button>
              <button onClick={() => setImportData(null)} className="btn-ghost !min-h-[40px] !px-5 text-sm flex-1">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
